import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useEffect, useImperativeHandle } from 'react';
import { useModal, useWindowSize, BREAKPOINTS_WIDTHS } from '@belong/common';
import { IconPlay } from '@belong/icons';
import clsx from 'clsx';
import { ButtonBase } from '../ButtonBase/ButtonBase.js';
import { Image } from '../Image/Image.js';
import { VideoPlayerModal } from './VideoPlayerModal.js';
import css from './VideoPlayerModal.module.css.js';

const VideoPlayer = /*#__PURE__*/forwardRef(({
  autoplay = true,
  videoSrc,
  posterSrc,
  playsInline = false,
  tracks = [],
  videoClassName = '',
  showPlayOverlay = true,
  loop = true,
  playIconStyle = 'default',
  buttonWrapperClassName,
  controls,
  onError,
  onCanPlay,
  preload,
  poster
}, ref) => {
  const videoRef = useRef(null);
  const modalVideoRef = useRef(null);
  const [isFullScreenVideoOpen, openFullScreenVideo, closeFullScreenVideo] = useModal(false);
  const {
    width
  } = useWindowSize();
  const isMobile = width <= BREAKPOINTS_WIDTHS.MD;
  const stopFullScreenVideo = () => {
    if (getFullscreenElement()) return;
    const video = isMobile ? modalVideoRef.current : videoRef.current;
    if (!video) return;
    if (!playsInline) {
      video.pause();
    }
    closeFullScreenVideo();
  };
  const playFullScreenVideo = () => {
    const video = videoRef.current;
    if (!video) return;
    if (playsInline) {
      if (!isMobile) video.pause();
      if (!showPlayOverlay) video.currentTime = 0;
    }
    openFullScreenVideo();
    if (isMobile) return;
    if (video.requestFullscreen) video.requestFullscreen();else if (video.msRequestFullscreen) video.msRequestFullscreen();else if (video.mozRequestFullScreen) video.mozRequestFullScreen();else if (video.webkitRequestFullscreen) video.webkitRequestFullscreen();else return console.warn('not supported');
    video.play();
  };
  useEffect(() => {
    const isSafari = navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1;
    const eventName = isSafari ? 'webkitfullscreenchange' : 'fullscreenchange';
    document.addEventListener(eventName, stopFullScreenVideo);
    return () => {
      document.removeEventListener(eventName, stopFullScreenVideo);
    };
  }, []);
  useEffect(() => {
    if (isFullScreenVideoOpen) {
      playFullScreenVideo();
    }
  }, [isFullScreenVideoOpen]);
  useImperativeHandle(ref, () => ({
    play: async () => {
      const video = videoRef.current;
      if (video) {
        await video.play();
      }
    },
    pause: () => {
      const video = videoRef.current;
      if (video) {
        video.pause();
      }
    }
  }), []);
  function VideoElement(ref, muted = false) {
    function getAreControlsVisible() {
      if (typeof controls === 'boolean') {
        return controls;
      }
      return isFullScreenVideoOpen || playsInline && !showPlayOverlay;
    }
    return jsxs("video", {
      className: clsx('block', videoClassName, !playsInline && (isFullScreenVideoOpen ? '' : 'hidden')),
      ref: ref,
      width: "100%",
      height: "100%",
      autoPlay: autoplay,
      controls: getAreControlsVisible(),
      muted: muted,
      onError: onError,
      ...(playsInline && {
        loop,
        playsInline: true,
        'webkit-playsinline': 1,
        'x5-playsinline': 1
      }),
      onCanPlay: onCanPlay,
      preload: preload,
      poster: poster,
      children: [jsx("source", {
        src: videoSrc,
        type: "video/mp4"
      }), tracks, "Sorry, your browser doesn't support embedded videos."]
    });
  }
  return jsxs(ButtonBase, {
    className: clsx('relative w-full', css.video_container, buttonWrapperClassName),
    onClick: openFullScreenVideo,
    children: [playsInline ? VideoElement(videoRef, isMobile || !isFullScreenVideoOpen) : jsx(Image, {
      src: posterSrc,
      className: "rounded",
      alt: ""
    }), showPlayOverlay && jsxs("div", {
      className: "absolute inset-0 flex items-center justify-center",
      children: [playIconStyle === 'default' ? jsx(IconPlay, {
        width: 60,
        className: "text-white opacity-80 transition duration-hover hover:opacity-100",
        title: "Icon Play Video",
        titleId: "iconPlay"
      }) : playIconStyle === 'green' && jsxs("div", {
        className: css.customPlayIcon,
        children: [jsx(Image, {
          src: "/gttl/lp_gtr_video_normal.svg",
          width: 80,
          height: 101,
          alt: "Play Icon",
          className: css.playIcon
        }), jsx(Image, {
          src: "/gttl/lp_gtr_video_hover.svg",
          width: 80,
          height: 101,
          alt: "Play Icon",
          className: css.playIconHover
        })]
      }), jsxs("div", {
        className: "absolute z-[-1]",
        children: [isMobile && jsx(VideoPlayerModal, {
          isOpen: isFullScreenVideoOpen,
          onDismiss: closeFullScreenVideo,
          children: VideoElement(modalVideoRef)
        }), !playsInline && !isMobile && isFullScreenVideoOpen && VideoElement(videoRef)]
      })]
    })]
  });
});
function getFullscreenElement() {
  return document.fullscreenElement || document.msFullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement;
}

export { VideoPlayer };
